/**
 * The parts the phone booking screens share (web-sign-in-checkout D12; shared-client-screens D3):
 * the rails the app's booking pages run themselves (the document lock, the viewport vars, the native
 * keyboard scroll kept off, the scroller kept clear of the keyboard), the Telegram mark for the
 * website's sign-in, and the app's payment page (PaymentStepScreen) on the website's payment step,
 * for the checkout and Pay now. The screens themselves are the app's.
 */
import { useRef } from 'react';
import type { BookingHeroProps } from '@/components/booking/BookingHero';
import { CardCheckoutPanel } from '@/components/payment/CardCheckoutPanel';
import { useClientLock } from '@/hooks/useClientLock';
import { useKeyboardAwareScroll } from '@/hooks/useKeyboardAwareScroll';
import { usePreventNativeKeyboardScroll } from '@/hooks/usePreventNativeKeyboardScroll';
import { useAppViewportCssVars } from '@/hooks/useVisualViewportCssVars';
import type { PhoneCallProps } from '@/pages/ConfirmPage/PhoneCallRow';
import { PaymentStepScreen } from '@/pages/PaymentPage/PaymentStepScreen';
import type { CheckoutEntry } from '@/types/checkout';

import { usePaymentStep } from '../pieces/payment-step/usePaymentStep';

export const TelegramIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21.2 4.3 2.9 11.4c-1 .4-1 1.2-.1 1.5l4.6 1.4 1.8 5.5c.2.6.6.7 1 .3l2.6-2.4 4.8 3.5c.8.5 1.4.2 1.6-.8l3.1-14.7c.3-1.2-.4-1.8-1.1-1.4z" />
    <line x1="7.4" y1="14.3" x2="17.6" y2="7.8" />
  </svg>
);

/** A booking step's rails, as the app's DetailsPage and PaymentPage run them; the scroller's ref and its keyboard handle. */
export function usePhoneStepRails() {
  useClientLock();
  useAppViewportCssVars();
  usePreventNativeKeyboardScroll();
  const scrollRef = useRef<HTMLDivElement>(null);
  const keyboardScroll = useKeyboardAwareScroll(scrollRef, { bottomPadding: 24 });
  return { scrollRef, keyboardScroll };
}

/** The app's payment page (PaymentStepScreen, frame D) on the website's payment step: card only (row P1). */
export function PhonePayment({
  entry,
  amount,
  hero,
  until,
  onBack,
  backDisabled,
  onPickNewTime = onBack,
  phoneCall,
}: {
  entry: CheckoutEntry;
  amount: number;
  hero: BookingHeroProps;
  until: string | null;
  onBack: () => void;
  backDisabled?: boolean;
  /** Pick a new time, when too close to the start to pay: one step back unless the surface says otherwise. */
  onPickNewTime?: () => void;
  /** At checkout, a by-phone service's call line (service-by-phone D5); Pay now never asks again. */
  phoneCall?: PhoneCallProps | null;
}) {
  const { state, actions, panel, flowPhase, resuming } = usePaymentStep({ entry, amount, onPickNewTime });
  const { scrollRef, keyboardScroll } = usePhoneStepRails();
  const confirming = flowPhase === 'confirming' || flowPhase === 'slowConfirm';
  return (
    <PaymentStepScreen
      hero={hero}
      onBack={onBack}
      backDisabled={backDisabled || confirming}
      scrollRef={scrollRef}
      reserved={until}
      phoneCall={phoneCall}
      amount={state.amount}
      view={state.view}
      phase={flowPhase}
      businessName={state.businessName}
      booked={entry.kind === 'postApproval'}
      // Back from a Stripe hand-off: no card form while its payment is read (a new one would replace it).
      methods={
        resuming ? null : (
          <CardCheckoutPanel
            key={panel.key}
            ref={panel.ref}
            entry={panel.entry}
            intentRequest={panel.intentRequest}
            onCompleteChange={panel.onCompleteChange}
            onIntentReady={panel.onIntentReady}
            onSubmitInFlightChange={panel.onSubmitInFlightChange}
            onPaymentSubmitted={panel.onPaymentSubmitted}
            onDecline={panel.onDecline}
            onError={panel.onError}
            onConflict={panel.onConflict}
            onCardFieldFocus={keyboardScroll.focusElement}
            onCardFieldBlur={keyboardScroll.blurElement}
          />
        )
      }
      decline={state.decline}
      // The website offers no other method beside the card (row P6).
      otherMethods={false}
      pay={{ method: 'CARD', onPress: actions.pay, disabled: !state.complete || state.paying }}
      onPickNewTime={actions.pickNewTime}
      pickNewTimeDisabled={backDisabled}
      onSlowDone={actions.done}
    />
  );
}
